/**
 * ScrapeKing Design System v2 - Input Components
 * "Data Extraction Terminal" Aesthetic
 */

import React, { FC, InputHTMLAttributes, TextareaHTMLAttributes, forwardRef } from 'react';
import styled, { css, keyframes } from 'styled-components';

// ═══════════════════════════════════════════════════════════════
// ANIMATIONS
// ═══════════════════════════════════════════════════════════════

const focusGlow = keyframes`
  0%, 100% {
    box-shadow: 0 0 0 2px rgba(0, 200, 224, 0.2);
  }
  50% {
    box-shadow: 0 0 0 4px rgba(0, 200, 224, 0.1);
  }
`;

// ═══════════════════════════════════════════════════════════════
// SHARED STYLES
// ═══════════════════════════════════════════════════════════════

const inputBaseStyles = css<{ $hasError?: boolean; $hasIcon?: boolean }>`
  width: 100%;
  font-family: var(--sk-font-body);
  font-size: var(--sk-text-sm);
  color: var(--sk-text-primary);
  background: var(--sk-bg-tertiary);
  border: 1px solid ${props => props.$hasError ? 'var(--sk-red-500)' : 'var(--sk-surface-border)'};
  border-radius: var(--sk-radius-default);
  transition: all 0.2s ease;

  &::placeholder {
    color: var(--sk-text-tertiary);
  }

  &:hover:not(:disabled):not(:focus) {
    border-color: ${props => props.$hasError ? 'var(--sk-red-400)' : 'var(--sk-surface-border-hover)'};
    background: var(--sk-bg-elevated);
  }

  &:focus {
    outline: none;
    border-color: ${props => props.$hasError ? 'var(--sk-red-500)' : 'var(--sk-cyan-500)'};
    background: var(--sk-bg-primary);
    box-shadow: ${props => props.$hasError
      ? '0 0 0 3px rgba(255, 0, 51, 0.15)'
      : '0 0 0 3px rgba(0, 200, 224, 0.15)'
    };
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background: var(--sk-bg-secondary);
  }
`;

// ═══════════════════════════════════════════════════════════════
// TEXT INPUT
// ═══════════════════════════════════════════════════════════════

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--sk-space-2);
  width: 100%;
`;

const Label = styled.label`
  font-family: var(--sk-font-display);
  font-size: var(--sk-text-xs);
  font-weight: 500;
  color: var(--sk-text-secondary);
  letter-spacing: 0.05em;
  text-transform: uppercase;
`;

const InputContainer = styled.div<{ $hasIcon?: boolean; $iconPosition?: 'left' | 'right' }>`
  position: relative;
  display: flex;
  align-items: center;
`;

const StyledInput = styled.input<{ $hasError?: boolean; $hasIcon?: boolean; $iconPosition?: 'left' | 'right' }>`
  ${inputBaseStyles}
  height: 44px;
  padding: 0 var(--sk-space-4);
  ${props => props.$hasIcon && props.$iconPosition === 'left' && 'padding-left: 44px;'}
  ${props => props.$hasIcon && props.$iconPosition === 'right' && 'padding-right: 44px;'}
`;

const InputIcon = styled.div<{ $position: 'left' | 'right' }>`
  position: absolute;
  ${props => props.$position === 'left' ? 'left: var(--sk-space-3);' : 'right: var(--sk-space-3);'}
  top: 50%;
  transform: translateY(-50%);
  color: var(--sk-text-tertiary);
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;

  svg {
    width: 20px;
    height: 20px;
  }
`;

const HelperText = styled.span<{ $error?: boolean }>`
  font-size: var(--sk-text-xs);
  color: ${props => props.$error ? 'var(--sk-red-400)' : 'var(--sk-text-tertiary)'};
`;

interface TextInputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string | boolean;
  helperText?: string;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
}

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(({
  label,
  error,
  helperText,
  icon,
  iconPosition = 'left',
  id,
  ...props
}, ref) => {
  const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;
  const hasError = Boolean(error);

  return (
    <InputWrapper>
      {label && <Label htmlFor={inputId}>{label}</Label>}
      <InputContainer $hasIcon={Boolean(icon)} $iconPosition={iconPosition}>
        {icon && <InputIcon $position={iconPosition}>{icon}</InputIcon>}
        <StyledInput
          ref={ref}
          id={inputId}
          $hasError={hasError}
          $hasIcon={Boolean(icon)}
          $iconPosition={iconPosition}
          {...props}
        />
      </InputContainer>
      {(error || helperText) && (
        <HelperText $error={hasError}>{error || helperText}</HelperText>
      )}
    </InputWrapper>
  );
});

TextInput.displayName = 'TextInput';

// ═══════════════════════════════════════════════════════════════
// TEXTAREA
// ═══════════════════════════════════════════════════════════════

const StyledTextarea = styled.textarea<{ $hasError?: boolean }>`
  ${inputBaseStyles}
  padding: var(--sk-space-3) var(--sk-space-4);
  min-height: 120px;
  resize: vertical;
  line-height: 1.6;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--sk-surface-border);
    border-radius: var(--sk-radius-full);
  }
`;

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  helperText?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(({
  label,
  error,
  helperText,
  id,
  ...props
}, ref) => {
  const inputId = id || `textarea-${Math.random().toString(36).substr(2, 9)}`;
  const hasError = Boolean(error);

  return (
    <InputWrapper>
      {label && <Label htmlFor={inputId}>{label}</Label>}
      <StyledTextarea
        ref={ref}
        id={inputId}
        $hasError={hasError}
        {...props}
      />
      {(error || helperText) && (
        <HelperText $error={hasError}>{error || helperText}</HelperText>
      )}
    </InputWrapper>
  );
});

Textarea.displayName = 'Textarea';

// ═══════════════════════════════════════════════════════════════
// SEARCH INPUT (Special variant)
// ═══════════════════════════════════════════════════════════════

const SearchWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 400px;
`;

const SearchInputStyled = styled.input<{ $hasValue: boolean }>`
  width: 100%;
  font-family: var(--sk-font-mono);
  font-size: var(--sk-text-sm);
  color: var(--sk-text-primary);
  background: var(--sk-bg-secondary);
  border: 1px solid var(--sk-surface-border);
  border-radius: var(--sk-radius-default);
  transition: all 0.2s ease;
  height: 44px;
  padding: 0 var(--sk-space-4);
  padding-left: 44px;
  padding-right: ${props => props.$hasValue ? '44px' : 'var(--sk-space-4)'};

  &::placeholder {
    color: var(--sk-text-tertiary);
  }

  &:hover:not(:disabled):not(:focus) {
    border-color: var(--sk-surface-border-hover);
    background: var(--sk-bg-elevated);
  }

  &:focus {
    outline: none;
    border-color: var(--sk-cyan-600);
    background: var(--sk-bg-primary);
    box-shadow: 0 0 0 3px rgba(0, 200, 224, 0.15);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: var(--sk-space-3);
  top: 50%;
  transform: translateY(-50%);
  color: var(--sk-text-tertiary);

  svg {
    width: 20px;
    height: 20px;
  }
`;

const ClearButton = styled.button`
  position: absolute;
  right: var(--sk-space-2);
  top: 50%;
  transform: translateY(-50%);
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--sk-surface-default);
  border: none;
  border-radius: var(--sk-radius-sm);
  color: var(--sk-text-tertiary);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--sk-surface-hover);
    color: var(--sk-text-secondary);
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const SearchIconSvg = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const ClearIconSvg = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

interface SearchInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  onClear?: () => void;
}

export const SearchInput: FC<SearchInputProps> = ({
  value,
  onClear,
  ...props
}) => {
  const hasValue = Boolean(value && String(value).length > 0);

  return (
    <SearchWrapper>
      <SearchIcon><SearchIconSvg /></SearchIcon>
      <SearchInputStyled
        type="text"
        $hasValue={hasValue}
        value={value}
        placeholder="Search..."
        {...props}
      />
      {hasValue && onClear && (
        <ClearButton onClick={onClear} type="button" aria-label="Clear search">
          <ClearIconSvg />
        </ClearButton>
      )}
    </SearchWrapper>
  );
};

// ═══════════════════════════════════════════════════════════════
// SELECT
// ═══════════════════════════════════════════════════════════════

const SelectContainer = styled.div`
  position: relative;
`;

const StyledSelect = styled.select<{ $hasError?: boolean }>`
  ${inputBaseStyles}
  height: 44px;
  padding: 0 44px 0 var(--sk-space-4);
  appearance: none;
  cursor: pointer;
  background-image: none;

  option {
    background: var(--sk-bg-tertiary);
    color: var(--sk-text-primary);
    padding: var(--sk-space-2);
  }
`;

const SelectArrow = styled.div`
  position: absolute;
  right: var(--sk-space-3);
  top: 50%;
  transform: translateY(-50%);
  color: var(--sk-text-tertiary);
  pointer-events: none;

  svg {
    width: 20px;
    height: 20px;
  }
`;

const ChevronDownSvg = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6,9 12,15 18,9" />
  </svg>
);

interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  helperText?: string;
  options: { value: string; label: string }[];
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(({
  label,
  error,
  helperText,
  options,
  id,
  ...props
}, ref) => {
  const inputId = id || `select-${Math.random().toString(36).substr(2, 9)}`;
  const hasError = Boolean(error);

  return (
    <InputWrapper>
      {label && <Label htmlFor={inputId}>{label}</Label>}
      <SelectContainer>
        <StyledSelect
          ref={ref}
          id={inputId}
          $hasError={hasError}
          {...props}
        >
          {options.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </StyledSelect>
        <SelectArrow><ChevronDownSvg /></SelectArrow>
      </SelectContainer>
      {(error || helperText) && (
        <HelperText $error={hasError}>{error || helperText}</HelperText>
      )}
    </InputWrapper>
  );
});

Select.displayName = 'Select';

// Alias for backward compatibility
export const Input = TextInput;

export default TextInput;
